import { useEffect, useMemo, useState } from 'react';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { RBAC_PERMISSIONS } from '@/constants/permissions';
import { CirclePlus } from 'lucide-react';
import { TableScrollFrame, WrappingCellText } from '@/components/common/DataTable';
import { STICKY_TABLE_HEADER_CLASS } from '@/components/common/DataTable/dataTable.constants';
import { ROW_SELECTED_BG } from '@/styles/dataTableBg';
import { DataTableEmpty } from '@/components/common/DataTable/DataTableEmpty';
import { DataTableLoading } from '@/components/common/DataTable/DataTableLoading';
import { EmptyValue } from '@/components/common/EmptyValue';
import { InfiniteLoadingRow } from '@/components/common/DataTable/InfiniteLoadingRow';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useAssignInstitutes } from '../hooks/useAssignInstitutes';
import type { UnassignedInstituteItem } from '../types/userInstituteMapping.types';
import { groupByDistrict } from '../utils/groupByDistrict';

interface UnassignedInstitutesTableProps {
  data: UnassignedInstituteItem[];
  isLoading: boolean;
  userId: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  loadMoreError?: boolean;
}

export function UnassignedInstitutesTable({
  data,
  isLoading,
  userId,
  onLoadMore,
  hasMore,
  isLoadingMore,
  loadMoreError,
}: UnassignedInstitutesTableProps) {
  const { can } = usePermissions();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const { mutate: assign, isPending: isAssigning } = useAssignInstitutes(() =>
    setSelectedIds(new Set()),
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIds(new Set());
  }, [userId]);

  const groups = useMemo(() => groupByDistrict(data), [data]);

  const sentinelRef = useInfiniteScroll<HTMLTableRowElement>({
    onLoadMore,
    hasMore: hasMore && !loadMoreError,
    isLoadingMore,
  });

  const allLoadedIds = useMemo(() => data.map((d) => d.id), [data]);
  const allSelected = allLoadedIds.length > 0 && allLoadedIds.every((id) => selectedIds.has(id));
  const selectionCount = selectedIds.size;

  function toggleOne(id: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allLoadedIds));
    }
  }

  function handleAssign() {
    if (selectionCount === 0) return;
    assign({ userId, instituteIds: Array.from(selectedIds) });
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-3">
      <TableScrollFrame className="lg:min-h-0 lg:flex-1">
        <table className="w-full min-w-160 table-fixed caption-bottom text-sm">
          <TableHeader className={STICKY_TABLE_HEADER_CLASS}>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-12 text-center">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={allLoadedIds.length === 0}
                  aria-label="Select all"
                  className="size-4 cursor-pointer rounded border-input accent-primary"
                />
              </TableHead>
              <TableHead className="w-[14%]">District</TableHead>
              <TableHead className="w-[40%]">Institute Name</TableHead>
              <TableHead className="w-[18%]">Institute Type</TableHead>
              <TableHead className="w-[16%]">Thana</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <DataTableLoading columnCount={5} rowCount={5} />
            ) : groups.length === 0 ? (
              <DataTableEmpty
                columnCount={5}
                message="No unassigned institute matches"
                hint="Try other filters."
              />
            ) : (
              <>
                {groups.flatMap((group) =>
                  group.institutes.map((institute, index) => {
                    const checked = selectedIds.has(institute.id);
                    return (
                      <TableRow
                        key={`${group.districtName}-${institute.id}`}
                        onClick={() => toggleOne(institute.id)}
                        className={cn(
                          'cursor-pointer border-b border-border/50 bg-card',
                          index === group.institutes.length - 1 && 'border-b-2',
                          checked && ROW_SELECTED_BG,
                        )}
                      >
                        <TableCell className="text-center">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(institute.id)}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={`Select ${institute.name}`}
                            className="size-4 cursor-pointer rounded border-input accent-primary"
                          />
                        </TableCell>
                        <TableCell className="align-top font-medium">
                          <WrappingCellText width="fill">
                            {index === 0 ? group.districtName : ''}
                          </WrappingCellText>
                        </TableCell>
                        <TableCell>
                          <WrappingCellText width="fill">{institute.name}</WrappingCellText>
                        </TableCell>
                        <TableCell>
                          <WrappingCellText width="fill">
                            {institute.instituteTypeName ?? <EmptyValue />}
                          </WrappingCellText>
                        </TableCell>
                        <TableCell>
                          <WrappingCellText width="fill">
                            {institute.thana?.name ?? <EmptyValue />}
                          </WrappingCellText>
                        </TableCell>
                      </TableRow>
                    );
                  }),
                )}
                {onLoadMore && (hasMore || loadMoreError) ? (
                  <InfiniteLoadingRow
                    ref={sentinelRef}
                    columnCount={5}
                    isLoading={isLoadingMore}
                    hasError={loadMoreError}
                    onRetry={onLoadMore}
                  />
                ) : null}
              </>
            )}
          </TableBody>
        </table>
      </TableScrollFrame>

      <div className="flex items-center justify-end gap-3 px-1">
        <span className="text-sm text-muted-foreground">{selectionCount} selected</span>
        <Button
          onClick={handleAssign}
          disabled={
            selectionCount === 0 || isAssigning || !can(RBAC_PERMISSIONS.INSTITUTE_MAPPING_CREATE)
          }
          className="h-10 gap-1.5"
        >
          <CirclePlus className="size-4" />
          {isAssigning ? 'Assigning...' : `Assign${selectionCount > 0 ? ` ${selectionCount}` : ''}`}
        </Button>
      </div>
    </div>
  );
}
